import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { FORM_OPTIONS, FORM_TEXT } from "@/config/site";
import { onPrefill } from "@/lib/prefill";
import { trackLead } from "@/lib/consent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  nome: "", email: "", telefono: "", comune: "", tipologia: "", situazione: "",
  numero_camere: "", fatturato_annuo: "", fatturato_obiettivo: "", occupazione_media: "", obiettivi: [], messaggio: "", privacy: false, website: "",
};

const validate = (f) => {
  const e = {};
  if (f.nome.trim().length < 2) e.nome = "Inserisci nome e cognome.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(f.email.trim())) e.email = "Inserisci un indirizzo email valido.";
  if ((f.telefono.match(/\d/g) || []).length < 6) e.telefono = "Inserisci un numero di telefono valido.";
  if (f.comune.trim().length < 2) e.comune = "Indica comune e provincia.";
  if (!f.tipologia) e.tipologia = "Seleziona la tipologia di progetto.";
  if (!f.situazione) e.situazione = "Seleziona la tua situazione.";
  if (f.numero_camere !== "" && (isNaN(Number(f.numero_camere)) || Number(f.numero_camere) < 0)) e.numero_camere = "Inserisci un numero valido.";
  if (f.fatturato_obiettivo !== "" && (isNaN(Number(f.fatturato_obiettivo)) || Number(f.fatturato_obiettivo) < 0)) e.fatturato_obiettivo = "Inserisci un importo valido.";
  if (f.occupazione_media !== "" && (isNaN(Number(f.occupazione_media)) || Number(f.occupazione_media) < 0 || Number(f.occupazione_media) > 100)) e.occupazione_media = "Inserisci una percentuale tra 0 e 100.";
  if (f.obiettivi.length === 0) e.obiettivi = "Seleziona almeno un obiettivo.";
  if (!f.messaggio.trim()) e.messaggio = "Raccontaci brevemente il tuo progetto.";
  if (f.messaggio.length > 2000) e.messaggio = "Massimo 2000 caratteri.";
  if (!f.privacy) e.privacy = "È necessario accettare l'informativa sulla privacy.";
  return e;
};

const Err = ({ id, msg }) => (msg ? <p id={id} role="alert" className="mt-1.5 text-xs text-red-700" data-testid={`${id}`}>{msg}</p> : null);

export const LeadForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [serverError, setServerError] = useState("");

  useEffect(() => onPrefill((p) => {
    setStatus("idle");
    setForm((f) => ({
      ...f,
      situazione: p.situazione ?? f.situazione,
      obiettivi: p.obiettivi ? Array.from(new Set([...f.obiettivi, ...p.obiettivi])) : f.obiettivi,
      numero_camere: p.numero_camere ?? f.numero_camere,
      fatturato_annuo: p.fatturato_annuo ?? f.fatturato_annuo,
      fatturato_obiettivo: p.fatturato_obiettivo ?? f.fatturato_obiettivo,
      messaggio: p.messaggio && (p.overwrite || !f.messaggio) ? p.messaggio : f.messaggio,
    }));
  }), []);

  const set = (k, v) => {
    setForm((f) => (k === "situazione" && v !== "Ho già una struttura" ? { ...f, [k]: v, fatturato_annuo: "", fatturato_obiettivo: "", occupazione_media: "" } : { ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };
  const toggleObiettivo = (o) => set("obiettivi", form.obiettivi.includes(o) ? form.obiettivi.filter((x) => x !== o) : [...form.obiettivi, o]);
  const isAttiva = form.situazione === "Ho già una struttura";

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(form);
    setErrors(errs);
    if (Object.keys(errs).length) {
      document.getElementById(`field-${Object.keys(errs)[0]}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setStatus("sending"); setServerError("");
    const payload = {
      ...form,
      numero_camere: form.numero_camere === "" ? null : Number(form.numero_camere),
      fatturato_annuo: isAttiva && form.fatturato_annuo ? form.fatturato_annuo : null,
      fatturato_obiettivo: isAttiva && form.fatturato_obiettivo !== "" ? Number(form.fatturato_obiettivo) : null,
      occupazione_media: isAttiva && form.occupazione_media !== "" ? Number(form.occupazione_media) : null,
    };
    try {
      await axios.post(`${API}/leads`, payload);
      trackLead(payload);
      setStatus("success");
    } catch (err) {
      const d = err?.response?.data?.detail;
      const is429 = err?.response?.status === 429;
      setServerError(typeof d === "string" ? d : is429 ? "Hai inviato troppe richieste. Riprova tra un'ora oppure contattaci via email o WhatsApp." : FORM_TEXT.error);
      setStatus("error");
    }
  };

  if (status === "success") {
    return (
      <div className="border border-bronze/50 bg-ivory p-8 sm:p-10" data-testid="lead-form-success">
        <span className="gold-line mb-5" />
        <p className="font-serif text-2xl sm:text-3xl text-forest leading-snug">{FORM_TEXT.success}</p>
      </div>
    );
  }

  const cls = (k) => `field ${errors[k] ? "field-error" : ""}`;

  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid="lead-form">
      <div className="grid gap-5 sm:grid-cols-2">
        <div id="field-nome"><label className="label" htmlFor="nome">Nome e cognome *</label>
          <input id="nome" className={cls("nome")} value={form.nome} onChange={(e) => set("nome", e.target.value)} autoComplete="name" data-testid="input-nome" /><Err id="error-nome" msg={errors.nome} /></div>
        <div id="field-email"><label className="label" htmlFor="email">Email *</label>
          <input id="email" type="email" className={cls("email")} value={form.email} onChange={(e) => set("email", e.target.value)} autoComplete="email" data-testid="input-email" /><Err id="error-email" msg={errors.email} /></div>
        <div id="field-telefono"><label className="label" htmlFor="telefono">Telefono *</label>
          <input id="telefono" type="tel" className={cls("telefono")} value={form.telefono} onChange={(e) => set("telefono", e.target.value)} autoComplete="tel" data-testid="input-telefono" /><Err id="error-telefono" msg={errors.telefono} /></div>
        <div id="field-comune"><label className="label" htmlFor="comune">Comune/provincia *</label>
          <input id="comune" className={cls("comune")} value={form.comune} onChange={(e) => set("comune", e.target.value)} data-testid="input-comune" /><Err id="error-comune" msg={errors.comune} /></div>
      </div>

      <div id="field-tipologia"><label className="label" htmlFor="tipologia">Tipologia di progetto *</label>
        <select id="tipologia" className={cls("tipologia")} value={form.tipologia} onChange={(e) => set("tipologia", e.target.value)} data-testid="select-tipologia">
          <option value="">Seleziona…</option>
          {FORM_OPTIONS.tipologie.map((t) => <option key={t} value={t}>{t}</option>)}
        </select><Err id="error-tipologia" msg={errors.tipologia} /></div>

      <fieldset id="field-situazione">
        <legend className="label">Situazione *</legend>
        <div className="grid sm:grid-cols-3 gap-2" role="radiogroup" data-testid="radiogroup-situazione">
          {FORM_OPTIONS.situazioni.map((s) => (
            <label key={s} className={`cursor-pointer border px-4 py-3 text-sm text-center transition-colors duration-200 ${form.situazione === s ? "bg-forest text-ivory border-forest" : "bg-white border-ivory-dark text-forest hover:border-forest"}`}
                   data-testid={`radio-situazione-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`} data-checked={form.situazione === s}>
              <input type="radio" name="situazione" value={s} checked={form.situazione === s} onChange={() => set("situazione", s)} className="sr-only" />{s}
            </label>
          ))}
        </div><Err id="error-situazione" msg={errors.situazione} />
      </fieldset>

      <div className="grid gap-5 sm:grid-cols-2">
        <div id="field-numero_camere"><label className="label" htmlFor="numero_camere">Numero camere</label>
          <input id="numero_camere" type="number" min="0" inputMode="numeric" className={cls("numero_camere")} value={form.numero_camere} onChange={(e) => set("numero_camere", e.target.value)} data-testid="input-numero-camere" /><Err id="error-numero_camere" msg={errors.numero_camere} /></div>
        {isAttiva && (
          <>
            <div id="field-fatturato_annuo" data-testid="field-fatturato"><label className="label" htmlFor="fatturato_annuo">Fatturato annuo attuale</label>
              <input id="fatturato_annuo" className={cls("fatturato_annuo")} value={form.fatturato_annuo} onChange={(e) => set("fatturato_annuo", e.target.value)} data-testid="input-fatturato" />
              <p className="mt-1.5 text-xs text-ink/55">indicativo, se la struttura è già attiva</p></div>
            <div id="field-fatturato_obiettivo" data-testid="field-fatturato-obiettivo"><label className="label" htmlFor="fatturato_obiettivo">Fatturato annuo desiderato (€)</label>
              <input id="fatturato_obiettivo" type="number" min="0" step="1000" inputMode="numeric" className={cls("fatturato_obiettivo")} value={form.fatturato_obiettivo} onChange={(e) => set("fatturato_obiettivo", e.target.value)} data-testid="input-fatturato-obiettivo" />
              <p className="mt-1.5 text-xs text-ink/55">qual è l'obiettivo di fatturato che riterresti ottimale per la tua struttura?</p><Err id="error-fatturato_obiettivo" msg={errors.fatturato_obiettivo} /></div>
            <div id="field-occupazione_media" data-testid="field-occupazione"><label className="label" htmlFor="occupazione_media">Occupazione media (%)</label>
              <input id="occupazione_media" type="number" min="0" max="100" inputMode="decimal" className={cls("occupazione_media")} value={form.occupazione_media} onChange={(e) => set("occupazione_media", e.target.value)} data-testid="input-occupazione" />
              <p className="mt-1.5 text-xs text-ink/55">se disponibile</p><Err id="error-occupazione_media" msg={errors.occupazione_media} /></div>
          </>
        )}
      </div>

      <fieldset id="field-obiettivi">
        <legend className="label">Obiettivo * <span className="normal-case tracking-normal font-normal text-ink/55">(uno o più)</span></legend>
        <div className="grid sm:grid-cols-2 gap-2" data-testid="checkboxes-obiettivi">
          {FORM_OPTIONS.obiettivi.map((o) => (
            <label key={o} className={`flex items-center gap-3 cursor-pointer border px-4 py-2.5 text-sm transition-colors duration-200 ${form.obiettivi.includes(o) ? "border-forest bg-ivory" : "border-ivory-dark bg-white hover:border-forest"}`}
                   data-testid={`checkbox-obiettivo-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`} data-checked={form.obiettivi.includes(o)}>
              <input type="checkbox" checked={form.obiettivi.includes(o)} onChange={() => toggleObiettivo(o)} className="h-4 w-4 accent-forest" />{o}
            </label>
          ))}
        </div><Err id="error-obiettivi" msg={errors.obiettivi} />
      </fieldset>

      <div id="field-messaggio"><label className="label" htmlFor="messaggio">Raccontaci brevemente il tuo progetto *</label>
        <textarea id="messaggio" rows={5} maxLength={2000} className={cls("messaggio")} value={form.messaggio} onChange={(e) => set("messaggio", e.target.value)} data-testid="textarea-messaggio" />
        <div className="flex justify-between"><Err id="error-messaggio" msg={errors.messaggio} /><span className="ml-auto mt-1.5 text-xs text-ink/45">{form.messaggio.length}/2000</span></div></div>

      <div id="field-privacy">
        <label className="flex items-start gap-3 text-sm text-ink/80 cursor-pointer">
          <input type="checkbox" checked={form.privacy} onChange={(e) => set("privacy", e.target.checked)} className="mt-0.5 h-4 w-4 accent-forest" data-testid="checkbox-privacy" />
          <span>{FORM_TEXT.privacy} <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="underline underline-offset-2 text-forest hover:text-bronze" data-testid="privacy-link">Informativa</Link></span>
        </label><Err id="error-privacy" msg={errors.privacy} />
      </div>

      <input type="text" name="website" value={form.website} onChange={(e) => set("website", e.target.value)} tabIndex={-1} autoComplete="off" aria-hidden="true" className="hidden" data-testid="honeypot-website" />

      {status === "error" && <p role="alert" className="border border-red-700/40 bg-red-50 px-4 py-3 text-sm text-red-800" data-testid="lead-form-error">{serverError}</p>}

      <button type="submit" disabled={status === "sending"} className="btn-primary w-full sm:w-auto disabled:opacity-70" data-testid="lead-form-submit-button">
        {status === "sending" ? <><Loader2 size={16} className="animate-spin" /> {FORM_TEXT.sending}</> : FORM_TEXT.submit}
      </button>
    </form>
  );
};
